function scrollToSection(section, direction) {
  const sections = Array.from(document.querySelectorAll('main section[id]'));
  const currentIndex = sections.indexOf(section);

  if (currentIndex === -1) {
    return;
  }

  const targetIndex = (currentIndex + direction + sections.length) % sections.length;
  const target = sections[targetIndex];

  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function formatEyebrow(value) {
  const words = String(value || '').trim().split(/\s+/, 2);

  if (words.length < 2) {
    return words[0] || '';
  }

  return `${words[0]} <em>${words[1]}</em>`;
}

function initHeroCarousels(root) {
  root.querySelectorAll('[data-hero-carousel]').forEach((hero) => {
    if (hero.dataset.heroInitialized === 'true') {
      return;
    }

    let slides = [];

    try {
      slides = JSON.parse(hero.dataset.heroSlides || '[]');
    } catch {
      slides = [];
    }

    if (slides.length === 0) {
      return;
    }

    hero.dataset.heroInitialized = 'true';

    const image = hero.querySelector('[data-hero-image]');
    const mobile = hero.querySelector('[data-hero-mobile]');
    const eyebrow = hero.querySelector('[data-hero-eyebrow]');
    const title = hero.querySelector('[data-hero-title]');
    const cta = hero.querySelector('[data-hero-cta]');
    const ctaLabel = hero.querySelector('[data-hero-cta-label]');
    const prev = hero.querySelector('[data-hero-prev]');
    const next = hero.querySelector('[data-hero-next]');
    let current = 0;
    let autoplayTimer = null;

    next?.classList.add('is-active');

    const show = (index) => {
      current = (index + slides.length) % slides.length;
      const slide = slides[current];

      hero.classList.add('is-changing');

      if (image) {
        image.src = slide.image || image.src;
        image.alt = slide.alt || '';
      }

      if (mobile) {
        mobile.srcset = slide.mobile || slide.image || mobile.srcset;
      }

      if (eyebrow) {
        eyebrow.innerHTML = formatEyebrow(slide.eyebrow);
      }

      if (title) {
        title.innerHTML = slide.title || '';
      }

      if (cta) {
        cta.href = slide.ctaUrl || '#';
      }

      if (ctaLabel) {
        ctaLabel.textContent = slide.ctaLabel || '';
      }

      window.setTimeout(() => hero.classList.remove('is-changing'), 260);
    };

    const setActiveArrow = (activeButton) => {
      if (activeButton === prev) {
        prev?.classList.add('is-active');
        next?.classList.remove('is-active');
        return;
      }

      next?.classList.add('is-active');
      prev?.classList.remove('is-active');
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const startAutoplay = () => {
      stopAutoplay();

      if (slides.length < 2) {
        return;
      }

      autoplayTimer = window.setInterval(() => {
        setActiveArrow(next);
        show(current + 1);
      }, 5500);
    };

    prev?.addEventListener('click', () => {
      stopAutoplay();
      setActiveArrow(prev);
      show(current - 1);
      startAutoplay();
    });

    next?.addEventListener('click', () => {
      stopAutoplay();
      setActiveArrow(next);
      show(current + 1);
      startAutoplay();
    });

    hero.addEventListener('mouseenter', stopAutoplay);
    hero.addEventListener('mouseleave', startAutoplay);
    hero.addEventListener('focusin', stopAutoplay);
    hero.addEventListener('focusout', startAutoplay);
    startAutoplay();
  });
}

function getCarouselStep(track) {
  const first = track.children[0];
  const second = track.children[1];

  if (first && second) {
    return second.offsetLeft - first.offsetLeft;
  }

  return first ? first.getBoundingClientRect().width : track.clientWidth;
}

function getCardCarouselIndex(track) {
  const step = getCarouselStep(track);

  if (!step) {
    return 0;
  }

  return Math.round(track.scrollLeft / step);
}

function scrollCarouselToIndex(track, index) {
  const slides = Array.from(track.children);
  const maxIndex = slides.length - 1;

  if (maxIndex < 0) {
    return 0;
  }

  const safeIndex = Math.min(Math.max(index, 0), maxIndex);
  const targetSlide = slides[safeIndex];

  if (!targetSlide) {
    return 0;
  }

  track.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
  return safeIndex;
}

function scrollCarousel(track, direction) {
  return scrollCarouselToIndex(track, getCardCarouselIndex(track) + direction);
}

function initSectionArrows(root) {
  root.querySelectorAll('[data-section-arrow]').forEach((button) => {
    const section = button.closest('section');
    const direction = button.dataset.sectionArrow === 'prev' ? -1 : 1;

    if (!section || button.dataset.arrowInitialized === 'true') {
      return;
    }

    button.dataset.arrowInitialized = 'true';
    button.addEventListener('click', () => scrollToSection(section, direction));
  });
}

function initCardCarousels(root) {
  root.querySelectorAll('[data-card-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-card-carousel-track]');

    if (!track || carousel.dataset.carouselInitialized === 'true') {
      return;
    }

    carousel.dataset.carouselInitialized = 'true';
    carousel.classList.add('is-card-carousel');

    const prevButtons = carousel.querySelectorAll('[data-card-carousel-prev]');
    const nextButtons = carousel.querySelectorAll('[data-card-carousel-next]');
    const thumbButtons = Array.from(carousel.querySelectorAll('[data-card-carousel-thumb]'));

    const setActiveArrow = (direction) => {
      if (direction < 0) {
        prevButtons.forEach((prevButton) => prevButton.classList.add('is-active'));
        nextButtons.forEach((nextButton) => nextButton.classList.remove('is-active'));
        return;
      }

      nextButtons.forEach((nextButton) => nextButton.classList.add('is-active'));
      prevButtons.forEach((prevButton) => prevButton.classList.remove('is-active'));
    };

    const syncThumbs = (activeIndex) => {
      thumbButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    const goToIndex = (index, direction = 1) => {
      const activeIndex = scrollCarouselToIndex(track, index);
      setActiveArrow(direction);
      syncThumbs(activeIndex);
    };

    syncThumbs(0);
    setActiveArrow(1);

    prevButtons.forEach((button) => {
      button.addEventListener('click', () => {
        goToIndex(getCardCarouselIndex(track) - 1, -1);
      });
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => {
        goToIndex(getCardCarouselIndex(track) + 1, 1);
      });
    });

    thumbButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        const currentIndex = getCardCarouselIndex(track);
        const direction = index < currentIndex ? -1 : 1;
        goToIndex(index, direction);
      });
    });

    track.addEventListener('scroll', () => {
      window.clearTimeout(track._cardCarouselScrollTimer);
      track._cardCarouselScrollTimer = window.setTimeout(() => {
        syncThumbs(getCardCarouselIndex(track));
      }, 90);
    });
  });
}

function initSeoMediaGalleries(root) {
  root.querySelectorAll('[data-seo-media]').forEach((gallery) => {
    if (gallery.dataset.seoMediaInitialized === 'true') {
      return;
    }

    const featured = gallery.querySelector('[data-seo-media-featured]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-seo-media-thumb]'));
    const prev = gallery.querySelector('[data-seo-media-prev]');
    const next = gallery.querySelector('[data-seo-media-next]');
    const images = [
      featured?.getAttribute('src'),
      ...thumbs.map((thumb) => thumb.getAttribute('src')),
    ].filter(Boolean);

    if (!featured || images.length < 2) {
      return;
    }

    gallery.dataset.seoMediaInitialized = 'true';
    next?.classList.add('is-active');

    let current = 0;

    const show = (index) => {
      current = (index + images.length) % images.length;
      featured.src = images[current];
    };

    prev?.addEventListener('click', () => {
      prev.classList.add('is-active');
      next?.classList.remove('is-active');
      show(current - 1);
    });

    next?.addEventListener('click', () => {
      next.classList.add('is-active');
      prev?.classList.remove('is-active');
      show(current + 1);
    });
  });
}

export function initClickableArrows(root = document) {
  initHeroCarousels(root);
  initSectionArrows(root);
  initCardCarousels(root);
  initSeoMediaGalleries(root);
}